import { useCallback, useEffect, useState } from "react";
import { fetchSessionCameraVideo, type SessionVideo } from "../lib/sessionVideos";

/**
 * Recorded camera video for one finished session. The caller only mounts this
 * for sessions that have ended — a live session's frames haven't been encoded.
 *
 * The MP4 sits behind a 1-hour SAS URL, so an expired link surfaces as a player
 * error; re-fetching re-signs it, which is what "Retry" does rather than
 * reloading the page.
 */
export function SessionVideoPanel({
  trialNumber,
  deviceId,
}: {
  trialNumber: string;
  deviceId: string;
}) {
  /** `undefined` while loading, `null` once we know there's no camera video. */
  const [video, setVideo] = useState<SessionVideo | null | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);
  const [playbackError, setPlaybackError] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    setPlaybackError(false);
    try {
      setVideo(await fetchSessionCameraVideo(trialNumber, deviceId));
    } catch (e) {
      setVideo(null);
      setErr((e as Error).message);
    }
  }, [trialNumber, deviceId]);

  useEffect(() => {
    setVideo(undefined);
    load();
  }, [load]);

  return (
    <div className="dp session-video" style={{ marginTop: 8 }}>
      <p className="session-video-title">CAMERA</p>

      {video === undefined ? (
        <p className="session-video-note">Loading video…</p>
      ) : err ? (
        <p className="session-video-note error">
          Couldn’t load video: {err}{" "}
          <button className="session-video-retry" onClick={load}>
            Retry
          </button>
        </p>
      ) : video === null ? (
        <p className="session-video-note">
          No camera video recorded for this session.
        </p>
      ) : playbackError ? (
        <p className="session-video-note error">
          This video couldn’t be played — its link may have expired.{" "}
          <button className="session-video-retry" onClick={load}>
            Retry
          </button>
        </p>
      ) : (
        <>
          <video
            // Remount when the URL changes so the element reloads its source
            // instead of holding the previous session's buffered frames.
            key={video.video_url}
            className="session-video-player"
            src={video.video_url}
            controls
            playsInline
            preload="metadata"
            onError={() => setPlaybackError(true)}
          />
          <p className="session-video-meta">
            {[
              video.duration_seconds != null && formatDuration(video.duration_seconds),
              video.frame_count != null && `${video.frame_count} frames`,
              video.fps != null && `${video.fps} fps`,
              video.start_offset_seconds != null &&
                video.start_offset_seconds !== 0 &&
                `starts ${formatOffset(video.start_offset_seconds)} session start`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </>
      )}
    </div>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s ? `${m}m ${s}s` : `${m}m`;
}

/** `start_offset_seconds` is signed on purpose — a session whose start_time was
 *  inferred can sit after its own first frame. Say which side rather than
 *  printing a bare negative number. */
function formatOffset(seconds: number): string {
  const abs = formatDuration(Math.abs(seconds));
  return seconds > 0 ? `${abs} after` : `${abs} before`;
}
